/* eslint-disable max-len */
/* eslint-disable max-lines-per-function */
import React, {useMemo, useState, useEffect, useRef} from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './imdTable.scss';
import axios from 'axios';
// import { Button } from 'primereact/button';
import { useAuth } from '../../../Hooks/useAuth';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { useWindowSize } from '../../../Hooks/useWindowSize'; //  1. Import the NEW hook
import { IMD_ROLLUP, PROJECT_STATUS_MASTER, IRD_USER_CUSTOMIZED_VIEW} from '../../../Api/resources/endPoint';

const buildServiceURL = (service, path) => {
  const url = appendServiceNameToURL(service);
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL + url + path;
  } else {
    const port = appendServicePortToURL(service);
    const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '');
    return `${baseUrl}:${port}${url}${path}`;
  }
};

const baseURL = buildServiceURL(serviceName.RESELLER_SERVICE, '/api' + IMD_ROLLUP);
const projectStatusURL = buildServiceURL(serviceName.RESELLER_SERVICE, '/api' + PROJECT_STATUS_MASTER);
const customizedViewBaseURL = buildServiceURL(serviceName.RESELLER_SERVICE, '/api' + IRD_USER_CUSTOMIZED_VIEW);

const IMDTable = () => {
  const { userProfile } = useAuth();
  const { height: windowHeight } = useWindowSize();
  const tableHeight = `${windowHeight - 200}px`;
  const [projectStatusList, setProjectStatusList] = useState([]);
  const tableContainerRef = useRef(null);

  // Fetch project statuses from API when component mounts
  useEffect(() => {
    const fetchProjectStatuses = async () => {
      try {
        const response = await axios.get(projectStatusURL, {
          headers: {
            token: userProfile.access_token,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
          },
        });

        if (response.data.success && Array.isArray(response.data.data)) {
          // Map response to {id, name} format for table-utility
          const statusList = response.data.data.map(status => ({
            id: status.project_status_id || status.project_status_code || status.id,
            name: status.project_status_name || status.name,
          }));
          setProjectStatusList(statusList);
        }
      } catch (error) {
        console.error('Error fetching project statuses:', error);
        setProjectStatusList([]);
      }
    };

    if (userProfile?.access_token) {
      fetchProjectStatuses();
    }
  }, [userProfile]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const applyCenterAlign = () => {
      const rows = container.querySelectorAll('.p-treetable-tbody tr');
      const columnStats = new Map();

      // First pass: Collect ALL cell values across all rows (including collapsed)
      rows.forEach((row) => {
        const cells = row.querySelectorAll('td');

        cells.forEach((cell) => {
          const colIndex = cell.cellIndex;

          if (!columnStats.has(colIndex)) {
            columnStats.set(colIndex, {
              number: 0,
              text: 0,
            });
          }

          const stats = columnStats.get(colIndex);

          // Normalize text
          const rawText = cell.textContent ?? '';
          const cleaned = rawText
            .replace(/\u00A0/g, ' ')
            .trim();

          if (cleaned === '') return;

          // normalize unicode minus
          const normalized = cleaned.replace(/[−–—]/g, '-');

          // Classification
          const isDash = normalized === '-';
          const numericCandidate = normalized
            .replace(/,/g, '')
            .replace(/\s+/g, '');

          const isNumber = /^-?\d+(\.\d+)?%?$/.test(numericCandidate);
          const isText = !isDash && !isNumber;

          // Collect stats (dash ignored)
          if (isNumber) stats.number++;
          if (isText) stats.text++;
        });
      });

      // Second pass: Apply alignment to cells based on column stats
      rows.forEach((row) => {
        const cells = row.querySelectorAll('td');

        cells.forEach((cell) => {
          const colIndex = cell.cellIndex;
          const stats = columnStats.get(colIndex);

          if (!stats) return;

          // Normalize text
          const rawText = cell.textContent ?? '';
          const cleaned = rawText
            .replace(/\u00A0/g, ' ')
            .trim();

          if (cleaned === '') return;

          // normalize unicode minus
          const normalized = cleaned.replace(/[−–—]/g, '-');

          // Classification
          const isDash = normalized === '-';
          const numericCandidate = normalized
            .replace(/,/g, '')
            .replace(/\s+/g, '');

          const isNumber = /^-?\d+(\.\d+)?%?$/.test(numericCandidate);

          // Apply center-align if column has numbers and no text, OR if current cell is dash/number
          const columnIsNumeric = stats.number > 0 && stats.text === 0;

          if (columnIsNumeric || isDash || isNumber) {
            cell.classList.add('center-align');
          } else {
            cell.classList.remove('center-align');
          }
        });
      });

      // Apply header alignment based on column stats
      const headerCells = container.querySelectorAll('.p-treetable-thead th');

      headerCells.forEach((header) => {
        const colIndex = header.cellIndex;
        const stats = columnStats.get(colIndex);

        if (!stats) {
          header.classList.remove('center-align');
          return;
        }

        // Header rule: numbers exist AND no text â†’ center
        const shouldCenterHeader = stats.number > 0 && stats.text === 0;

        if (shouldCenterHeader) {
          header.classList.add('center-align');
        } else {
          header.classList.remove('center-align');
        }
      });
    };

    const observer = new MutationObserver(applyCenterAlign);

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    applyCenterAlign();

    return () => observer.disconnect();
  }, []);


  const handleCellClick = ({ fieldPath, rowData }) => {
    if (fieldPath === 'structure'){
      const redirectUrl = rowData.projectUrl;
      // Open the redirect URL in a new tab
      window.open(redirectUrl, '_blank');
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const cleanupFns = [];

    // Calculate scrollbar width dynamically
    const getScrollbarWidth = () => {
      const outer = document.createElement('div');
      outer.style.visibility = 'hidden';
      outer.style.overflow = 'scroll';
      document.body.appendChild(outer);
      const inner = document.createElement('div');
      outer.appendChild(inner);
      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
      document.body.removeChild(outer);
      return scrollbarWidth || 17; // fallback to 17px
    };

    const scrollbarWidth = getScrollbarWidth();
    container.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);

    const setup = () => {
      const views = container.querySelectorAll('.p-treetable-scrollable-view');
      if (views.length < 2) return false;

      const frozenBody = views[0].querySelector('.p-treetable-scrollable-body');
      const scrollableBody = views[1].querySelector('.p-treetable-scrollable-body');
      const headers = container.querySelectorAll('.p-treetable-scrollable-header');

      if (!frozenBody || !scrollableBody) return false;

      // Debounced height sync to prevent ResizeObserver loop
      let heightSyncTimeout = null;
      const syncHeights = () => {
        clearTimeout(heightSyncTimeout);
        heightSyncTimeout = setTimeout(() => {
          requestAnimationFrame(() => {
            const frozenTable = frozenBody.querySelector('table');
            const scrollableTable = scrollableBody.querySelector('table');
            if (frozenTable && scrollableTable) {
              const frozenHeight = frozenTable.offsetHeight;
              const scrollableHeight = scrollableTable.offsetHeight;
              // Only update if there's a meaningful difference
              if (Math.abs(frozenHeight - scrollableHeight) > 1) {
                const maxHeight = Math.max(frozenHeight, scrollableHeight);
                frozenTable.style.minHeight = `${maxHeight}px`;
                scrollableTable.style.minHeight = `${maxHeight}px`;
              }
            }
          });
        }, 100);
      };

      // Initial sync
      syncHeights();

      // Watch for content changes with debouncing
      const resizeObserver = new ResizeObserver(() => {
        syncHeights();
      });

      resizeObserver.observe(frozenBody);
      resizeObserver.observe(scrollableBody);

      cleanupFns.push(() => {
        clearTimeout(heightSyncTimeout);
        resizeObserver.disconnect();
      });

      let syncing = false;

      /* Proportional sync with precise alignment */
      const syncScroll = (from, to) => () => {
        if (syncing) return;
        syncing = true;

        // Use identical calculation for both directions
        const fromMax = from.scrollHeight - from.clientHeight;
        const toMax = to.scrollHeight - to.clientHeight;

        // If one can't scroll, align to top
        if (fromMax <= 0 || toMax <= 0) {
          to.scrollTop = 0;
          requestAnimationFrame(() => (syncing = false));
          return;
        }

        // Calculate percentage and apply with rounding to prevent subpixel drift
        const scrollPercentage = from.scrollTop / fromMax;
        const targetScroll = Math.round(scrollPercentage * toMax);

        to.scrollTop = Math.max(0, Math.min(targetScroll, toMax));

        requestAnimationFrame(() => (syncing = false));
      };

      const syncFromScrollable = syncScroll(scrollableBody, frozenBody);
      const syncFromFrozen = syncScroll(frozenBody, scrollableBody);

      /* Wheel handler (vertical + horizontal) */
      const wheelHandler = (target) => (event) => {
        const { deltaX, deltaY } = event;

        const canScrollY
        = (deltaY < 0 && target.scrollTop > 0)
        || (deltaY > 0 && target.scrollTop + target.clientHeight < target.scrollHeight);

        const canScrollX
        = (deltaX < 0 && target.scrollLeft > 0)
        || (deltaX > 0 && target.scrollLeft + target.clientWidth < target.scrollWidth);

        if (canScrollX || canScrollY) {
          event.preventDefault();
        }

        if (canScrollY) target.scrollTop += deltaY;
        if (canScrollX) target.scrollLeft += deltaX;
      };

      const frozenWheel = wheelHandler(scrollableBody);
      const headerWheel = wheelHandler(scrollableBody);

      /* Attach listeners */
      scrollableBody.addEventListener('scroll', syncFromScrollable);
      frozenBody.addEventListener('scroll', syncFromFrozen);

      frozenBody.addEventListener('wheel', frozenWheel, { passive: false });
      headers.forEach(header =>
        header.addEventListener('wheel', headerWheel, { passive: false }),
      );

      /* Cleanup */
      cleanupFns.push(() => {
        scrollableBody.removeEventListener('scroll', syncFromScrollable);
        frozenBody.removeEventListener('scroll', syncFromFrozen);
        frozenBody.removeEventListener('wheel', frozenWheel);
        headers.forEach(header =>
          header.removeEventListener('wheel', headerWheel),
        );
      });

      return true;
    };

    const observer = new MutationObserver(() => {
      if (setup()) observer.disconnect();
    });

    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      cleanupFns.forEach(fn => fn());
    };
  }, [userProfile, baseURL]);

  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL,
        projectStatusApiUrl: projectStatusURL,
        customizedViewBaseURL: customizedViewBaseURL,
        enableProjectStatusFilter: true, //  Enable the status filter
        useSnakeCaseParams: true, //  Convert projectStatus â†’ project_status for backend
        headers: {
          token: userProfile?.access_token || '',
        },
        additionalPayloadData: {
          statusList: projectStatusList,
        },
        userInfo: {
          id: userProfile?.id || '',
          emailId: userProfile?.email || '',
          permissions: userProfile?.permissions || [],
          roleDetails: userProfile?.roleDetails || [],
          employeeId: userProfile?.employeeId || '',
        },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
        },
        showToolBar: true,
        showPagination: false,
        handler: {
          onCellClickHandler: handleCellClick,
        },
        toolBarSettings: {
          showAddNew: false,
          showExpandCollapse: false,
          showDownloadExcel: false,
          downloadExcelSheetName: 'Integrated Revenue Dashboard',
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showSaveView: false,
          showToolBarHeading: true,
          toolBarHeading: 'Integrated Revenue Dashboard',
          showReset: false,
          additionalElements: [],
        },
      },
      tableSettings: {
        enableTreeTable: true,
        size: 'large',
        loaderSkeleton: {
          rows: 5,
          bodyCells: 1,
          columns: 8,
        },
        pageSize: 10,
        showPageSizeSelector: false,
        paginationConfig: {
          page: 1,
          limit: 5,
          rowsPerPageOptions: [10, 15, 25, 50],
        },
        showExpandCollapse: true,
        // tableConfig,
        tableScrollHeight: tableHeight,
        maxFrozenColumns: {
          desktop: 4,
          tablet: 2,
          mobile: 1,
        },
      },
    }),
    [baseURL, userProfile, tableHeight, projectStatusList],
  );
  return (
    <div className="imd-outer-layer">
      <div className='imd-table-container' ref={tableContainerRef}>
        <div className='imd-table-utility'>
          <TableUtility configuration={configuration}/>
        </div>
      </div>
    </div>
  );
};
export default IMDTable;
